<template>
    <div
        @wheel="handleWheel"
        @mousemove="handleMove"
        @mousedown="mousePressed = true"
        @mouseup="mousePressed = false"
        class="diagram-viewer"
        :class="{ 'is-grabbing': mousePressed }"
    >
        <div class="wrapper" ref="wrapperEl">
            <BlockConnectors :blocks="blocks.blocks" :metas="blocks.metas" />
            <Block
                @openContextMenu="(e) => $emit('openContextMenu', e)"
                v-for="block in blocks.blocks"
                :key="block.id"
                :block="block"
                :metas="blocks.metas"
                :active="selectedBlock === block.id"
                @focusBlock="focusBlock"
                :automation="automation"
                :flattedDiagram="flattedDiagram"
                :movingMode="movingMode"
                :movingBlock="movingBlock"
                :changeMoveMode="changeMoveMode"
                :moveBlock="moveBlock"
                :copyingMode="copyingMode"
                :copyingBlock="copyingBlock"
                :changeCopyMode="changeCopyMode"
                :copyBlock="copyBlock"
                :showNotification="showNotification"
                :dragMode="dragMode"
                :addBlockToDiagram="addBlockToDiagram"
                :draggingType="draggingType"
                :collapsed="false"
                :collapsedAll="false"
                :start-test-from-here="startTestFromHere"
                :selected="selectedBlockId === block.id"
                :preview-mode="previewMode"
                :getAllowedBlocks="getAllowedBlocks"
                @updateAutomation="($event) => $emit('updateAutomation', $event)"
            />
        </div>

        <!-- Mini Map -->
        <div class="automation-minimap-container" v-if="minimapVisible && blocks.blocks.length > 5">
            <div class="automation-minimap-content" @click="onMinimapClick">
                <svg :width="minimapWidth" :height="minimapHeight">
                    <defs>
                        <linearGradient id="automation-gradient-start" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#10b981;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#059669;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-flow" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#8b5cf6;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#7c3aed;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-message" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#2563eb;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-input" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#f59e0b;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#d97706;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-attachment" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#f97316;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#ea580c;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-condition" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#ef4444;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#dc2626;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-success" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#22c55e;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#16a34a;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-failure" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#f87171;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#ef4444;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-fallback" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#94a3b8;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#64748b;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-transfer" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#ec4899;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#db2777;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="automation-gradient-default" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#9ca3af;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#6b7280;stop-opacity:1" />
                        </linearGradient>
                    </defs>

                    <!-- Basit önizleme için tüm blokları göster -->
                    <rect
                        v-for="block in blocks.blocks"
                        :key="'automation-minimap-' + block.id"
                        :x="getMinimapX(blocks.metas[block.id].x)"
                        :y="getMinimapY(blocks.metas[block.id].y)"
                        :width="getMinimapWidth(blocks.metas[block.id].width)"
                        :height="getMinimapHeight(blocks.metas[block.id].height)"
                        :class="['automation-minimap-block', { 'automation-selected': selectedBlockId === block.id }]"
                        :fill="getBlockColor(block.type)"
                        rx="2"
                        ry="2"
                    />

                    <rect
                        :x="viewportX"
                        :y="viewportY"
                        :width="viewportWidth"
                        :height="viewportHeight"
                        class="automation-minimap-viewport"
                        fill="rgba(59, 130, 246, 0.1)"
                        stroke="#3b82f6"
                        stroke-width="2"
                        stroke-dasharray="4,2"
                        rx="6"
                        ry="6"
                    />
                </svg>
            </div>
        </div>
    </div>
</template>

<script>
import { log2 } from "../../../../../../utils/log2";
import _ from "lodash";
import { mapGetters } from "vuex";
import BlockConnectors from "./BlockConnectors";
import Block from "./Block";
import AUTOMATION_BLOCK_TYPES from "../../../AutomationBlockTypes";
import * as d3 from "d3";
import SYSTEM_PUBSUB from "../../../../../../utils/systemPubSub";
import { filter } from "rxjs/operators";

const log = log2("AutomationDiagramViewer");
export default {
    name: "AutomationDiagramViewer",
    props: [
        "automation",
        "scale",
        "translate",
        "translating",
        "selectedBlock",
        "navigation",
        "navigateToBlock",
        "movingMode",
        "movingBlock",
        "changeMoveMode",
        "moveBlock",
        "copyingMode",
        "copyingBlock",
        "changeCopyMode",
        "copyBlock",
        "flattedDiagram",
        "showNotification",
        "dragMode",
        "addBlockToDiagram",
        "draggingType",
        "startTestFromHere",
        "selectedBlockId",
        "customAttributes",
        "previewMode",
        "getAllowedBlocks",
    ],
    components: { Block, BlockConnectors },
    data() {
        return {
            mousePressed: false,
            sub: null,
            minimapVisible: false,
            minimapWidth: 200,
            minimapHeight: 150,
            minimapUpdateTimer: null,
            cachedMinimapData: null,
            minimapClickThrottle: false,
            // Internal transform state - NOT reactive for performance
            _internalTranslate: { x: 0, y: 0 },
            _internalScale: 1,
            _rafId: null,
        };
    },

    computed: {
        ...mapGetters({
            settings: "CB/settings",
        }),

        // Mini-map computed properties (cached for performance)
        minimapBounds() {
            if (!this.minimapVisible) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
            
            if (!this.blocks.metas || Object.keys(this.blocks.metas).length === 0) {
                return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
            }

            // Cache için key oluştur
            const cacheKey = JSON.stringify(Object.keys(this.blocks.metas));
            if (this.cachedMinimapData && this.cachedMinimapData.cacheKey === cacheKey) {
                return this.cachedMinimapData.bounds;
            }

            const metas = Object.values(this.blocks.metas);
            const minX = Math.min(...metas.map(m => m.x));
            const maxX = Math.max(...metas.map(m => m.x + m.width));
            const minY = Math.min(...metas.map(m => m.y));
            const maxY = Math.max(...metas.map(m => m.y + m.height));

            const bounds = { minX, maxX, minY, maxY };
            
            // Cache'e kaydet
            this.cachedMinimapData = { cacheKey, bounds };
            
            return bounds;
        },

        minimapScale() {
            const bounds = this.minimapBounds;
            const diagramWidth = bounds.maxX - bounds.minX;
            const diagramHeight = bounds.maxY - bounds.minY;

            if (diagramWidth === 0 || diagramHeight === 0) return 1;

            const scaleX = (this.minimapWidth - 16) / diagramWidth;
            const scaleY = (this.minimapHeight - 16) / diagramHeight;

            return Math.min(scaleX, scaleY, 0.5);
        },

        viewportX() {
            const bounds = this.minimapBounds;
            // Use internal state for more accurate viewport position
            const currentViewX = -this._internalTranslate.x / this._internalScale;
            return (currentViewX - bounds.minX) * this.minimapScale + 8;
        },

        viewportY() {
            const bounds = this.minimapBounds;
            const currentViewY = -this._internalTranslate.y / this._internalScale;
            return (currentViewY - bounds.minY) * this.minimapScale + 8;
        },

        viewportWidth() {
            const viewWidth = window.innerWidth / this._internalScale;
            return Math.min(viewWidth * this.minimapScale, this.minimapWidth - 16);
        },

        viewportHeight() {
            const viewHeight = window.innerHeight / this._internalScale;
            return Math.min(viewHeight * this.minimapScale, this.minimapHeight - 16);
        },

        blocks: function () {
            const startPoint = { x: 100, y: 300 };
            const blockSizes = { width: 190, height: 40 };
            const squareBlockSizes = { width: 40, height: 40 };
            const squareBlockTypes = [AUTOMATION_BLOCK_TYPES.USER_INPUT, AUTOMATION_BLOCK_TYPES.ATTACHMENT_INPUT];
            const rootBlock = this.navigation.selectedFlowPath
                ? _.get(this.automation.diagram, this.navigation.selectedFlowPath)
                : this.automation.diagram;

            const metas = {
                [rootBlock.id]: {
                    width: squareBlockTypes.includes(rootBlock.type) ? squareBlockSizes.width : blockSizes.width,
                    height: squareBlockTypes.includes(rootBlock.type) ? squareBlockSizes.height : blockSizes.height,
                    name: rootBlock.name,
                    isRoot: true,
                    isSquare: squareBlockTypes.includes(rootBlock.type),
                },
            };
            const rootMeta = metas[rootBlock.id];

            const flatBlock = (block, parentMeta, isRoot) => {
                const isSquare = squareBlockTypes.includes(block.type);
                const sizes = isSquare ? squareBlockSizes : blockSizes;
                const meta = isRoot
                    ? parentMeta
                    : {
                          width: sizes.width,
                          height: sizes.height,
                          name: block.name,
                          isRoot: false,
                          isSquare,
                      };

                metas[block.id] = meta;
                // log("metas", metas);
                const nextFlow = !isRoot && block.type === "FLOW";
                const collapsedBlock = []; //todo refactor
                const items = [
                    block,
                    ...(nextFlow || (collapsedBlock.includes(block.id) && !["START_POINT"].includes(block.type))
                        ? []
                        : block.children
                              .map((childBlock, i) => {
                                  return !!childBlock ? flatBlock(childBlock, meta, false) : null;
                              })
                              .filter(Boolean)),
                ];

                return _.flatten(items);
            };

            const items = _.flatten(flatBlock(rootBlock, rootMeta, true));

            const calculatePositions = () => {
                const root = _.cloneDeep(rootBlock);
                const flatFlows = (block, isRoot = false) => {
                    if (!isRoot && block.type === AUTOMATION_BLOCK_TYPES.FLOW) block.children = [];
                    block.children = block.children.filter(Boolean);
                    block.children.filter(Boolean).forEach(flatFlows);
                };
                flatFlows(root, true);

                console.log("calculatePositions", root);
                let treemap = d3.tree().nodeSize([blockSizes.height * 2.5, blockSizes.width + 80]);

                let nodes = d3.hierarchy(root, function (d) {
                    return d.children;
                });
                nodes = treemap(nodes);
                // log("nodes", nodes);
                const traverse = (block) => {
                    // log("traverse", block, block.children);
                    if (!!metas[block.data.id]) {
                        metas[block.data.id].x = block.y;
                        metas[block.data.id].y = block.x;
                    }
                    (block.children || []).filter(Boolean).forEach((child) => {
                        traverse(child);
                    });
                };
                traverse(nodes);
            };
            calculatePositions();

            if (!this.translating) this.$emit("blockMetasCalculated", metas);

            log("metas", {
                metas,
                items,
            });
            return {
                blocks: items,
                metas,
            };
        },
    },
    methods: {
        // === PERFORMANCE CRITICAL: Direct DOM manipulation for transform ===
        // This bypasses Vue's reactivity system for smooth 60fps panning/zooming
        _applyTransformToDOM() {
            if (this._rafId) {
                cancelAnimationFrame(this._rafId);
            }
            this._rafId = requestAnimationFrame(() => {
                const el = this.$refs.wrapperEl;
                if (el) {
                    el.style.transform = `scale(${this._internalScale}) translate(${this._internalTranslate.x}px, ${this._internalTranslate.y}px)`;
                }
                this._rafId = null;
            });
        },
        
        // Handle transform updates locally without triggering Vue re-renders
        _updateTransformLocal(deltaX, deltaY) {
            this._internalTranslate.x += deltaX / this._internalScale;
            this._internalTranslate.y += deltaY / this._internalScale;
            this._applyTransformToDOM();
        },
        
        _setTransformLocal(x, y) {
            this._internalTranslate.x = x;
            this._internalTranslate.y = y;
            this._applyTransformToDOM();
        },
        
        _updateScaleLocal(newScale) {
            // Clamp scale between 0.5 and 1.5
            this._internalScale = Math.max(0.5, Math.min(1.5, newScale));
            this._applyTransformToDOM();
        },
        
        // Debounced sync to parent - only for state persistence, not for rendering
        _debouncedSyncTranslate: _.debounce(function() {
            // Sync internal state to parent (for persistence/external use)
            // This does NOT cause re-render because we handle transform via DOM
            this.$emit("updateTranslate", { 
                x: this._internalTranslate.x - this.translate.x,
                y: this._internalTranslate.y - this.translate.y 
            });
        }, 150),
        
        _debouncedSyncScale: _.debounce(function() {
            this.$emit("updateScale", this._internalScale);
        }, 150),
        
        // Mini-map methods
        handleMinimapToggle() {
            this.minimapVisible = !this.minimapVisible;
            if (!this.minimapVisible) {
                // Mini-map kapatıldığında cache'i temizle
                this.cachedMinimapData = null;
            }
        },

        // Optimized minimap update with requestAnimationFrame
        debouncedMinimapUpdate() {
            if (this.minimapUpdateTimer) {
                clearTimeout(this.minimapUpdateTimer);
            }
            this.minimapUpdateTimer = setTimeout(() => {
                if (this.minimapVisible) {
                    requestAnimationFrame(() => {
                        this.$forceUpdate();
                    });
                }
            }, 150); // Daha uzun debounce
        },

        getMinimapX(x) {
            const bounds = this.minimapBounds;
            return (x - bounds.minX) * this.minimapScale + 8;
        },

        getMinimapY(y) {
            const bounds = this.minimapBounds;
            return (y - bounds.minY) * this.minimapScale + 8;
        },

        getMinimapWidth(width) {
            return Math.max(width * this.minimapScale, 2);
        },

        getMinimapHeight(height) {
            return Math.max(height * this.minimapScale, 2);
        },

        getBlockColor(blockType) {
            const colors = {
                'START_POINT': 'url(#automation-gradient-start)',
                'FLOW': 'url(#automation-gradient-flow)',
                'MESSAGE': 'url(#automation-gradient-message)',
                'BOT_RESPONSE': 'url(#automation-gradient-message)',
                'USER_INPUT': 'url(#automation-gradient-input)',
                'ATTACHMENT_INPUT': 'url(#automation-gradient-attachment)',
                'CONDITION': 'url(#automation-gradient-condition)',
                'FILTER': 'url(#automation-gradient-condition)',
                'SUCCESS': 'url(#automation-gradient-success)',
                'FAILURE': 'url(#automation-gradient-failure)',
                'FALLBACK': 'url(#automation-gradient-fallback)',
                'TRANSFER_CHAT': 'url(#automation-gradient-transfer)',
                'DEFAULT_FALLBACK': 'url(#automation-gradient-fallback)',
                'TIMEOUT': 'url(#automation-gradient-fallback)',
                'DEFAULT_TIMEOUT': 'url(#automation-gradient-fallback)'
            };
            return colors[blockType] || 'url(#automation-gradient-default)';
        },

        onMinimapClick(event) {
            // Performans için agresif throttle
            if (this.minimapClickThrottle) return;
            this.minimapClickThrottle = true;
            setTimeout(() => { this.minimapClickThrottle = false; }, 300);

            const rect = event.currentTarget.getBoundingClientRect();
            const clickX = event.clientX - rect.left - 8;
            const clickY = event.clientY - rect.top - 8;

            const bounds = this.minimapBounds;
            const realX = (clickX / this.minimapScale) + bounds.minX;
            const realY = (clickY / this.minimapScale) + bounds.minY;

            const newTranslateX = -realX * this._internalScale + window.innerWidth / 2;
            const newTranslateY = -realY * this._internalScale + window.innerHeight / 2;

            // Update locally first for immediate feedback
            this._setTransformLocal(newTranslateX, newTranslateY);
            
            // Then sync to parent
            this.$emit("setTranslate", {
                x: newTranslateX,
                y: newTranslateY
            });
        },

        handleWheel(e) {
            e.preventDefault();
            const settings = {}; //todo refactor
            
            if (settings?.navigation === "mouse" || e.ctrlKey) {
                // Zoom - update scale locally
                let value = Math.min(200, Math.abs(e.deltaY));
                let newDelta = (value / 200 + 0.5) / 10;
                newDelta = e.deltaY <= 0 ? newDelta : newDelta * -1;
                const nextScale = this._internalScale + newDelta;
                
                // Update locally (no Vue re-render)
                this._updateScaleLocal(nextScale);
                
                // Sync to parent only occasionally for state persistence
                this._debouncedSyncScale();
            } else {
                // Pan - update translate locally (no Vue re-render)
                this._updateTransformLocal(-e.deltaX, -e.deltaY);
                
                // Sync to parent only occasionally for state persistence
                this._debouncedSyncTranslate();
            }
        },
        handleMove(e) {
            e.preventDefault();
            if (!this.mousePressed) return;
            
            // Update locally (no Vue re-render) - smooth 60fps
            this._updateTransformLocal(e.movementX, e.movementY);
            
            // Sync to parent only occasionally for state persistence
            this._debouncedSyncTranslate();
        },
        focusBlock(e, block) {
            log("focusBlock block", block);
            if (
                [
                    AUTOMATION_BLOCK_TYPES.START_POINT,
                    AUTOMATION_BLOCK_TYPES.DEFAULT_FALLBACK,
                    AUTOMATION_BLOCK_TYPES.SUCCESS,
                    AUTOMATION_BLOCK_TYPES.FAILURE,
                    AUTOMATION_BLOCK_TYPES.FALLBACK,
                    AUTOMATION_BLOCK_TYPES.TRANSFER_CHAT,
                ].includes(block.type)
            )
                return;
            if (block.type === "FLOW") {
                this.navigateToBlock(block);
                return;
            }
            this.$emit("openModal", block);
            log("focusBlock x, y");
            const { x, y } = this.blocks.metas[block.id];
            this.$emit("setTranslate", {
                x: -x + window.innerWidth / 2 - 250,
                y: -y + window.innerHeight / 2 - 50,
            });
        },
    },
    mounted() {
        // Initialize internal transform state from props
        this._internalTranslate = { x: this.translate.x, y: this.translate.y };
        this._internalScale = this.scale;
        
        // Apply initial transform directly to DOM (bypassing Vue reactivity)
        this._applyTransformToDOM();
        
        this.$emit("blocks-metas", this.blocks.metas);
        this.sub = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.CB_FOCUS_BLOCK))
            .subscribe(({ payload }) => {
                log("mounted blocks focusBlock start");
                if (!!this.blocks.metas[payload.blockId]) {
                    log("mounted blocks focusBlock start if", {
                        b: this.blocks,
                        m: this.blocks.metas,
                        mp: this.blocks.metas[payload.blockId],
                        p: payload,
                    });
                    const { x, y } = this.blocks.metas[payload.blockId];
                    log("mounted blocks focusBlock x, y", { x, y });
                    this.$emit("setTranslate", {
                        x: -x + window.innerWidth / 2 - 250,
                        y: -y + window.innerHeight / 2 - 50,
                    });
                }
            });
    },
    updated() {},
    beforeDestroy() {
        this.sub.unsubscribe();
        if (this.minimapUpdateTimer) {
            clearTimeout(this.minimapUpdateTimer);
        }
        if (this._rafId) {
            cancelAnimationFrame(this._rafId);
        }
        // Cancel debounced functions
        if (this._debouncedSyncTranslate?.cancel) {
            this._debouncedSyncTranslate.cancel();
        }
        if (this._debouncedSyncScale?.cancel) {
            this._debouncedSyncScale.cancel();
        }
    },
    watch: {
        // Sync from parent props to internal state (for external setTranslate calls)
        translate: {
            handler(newVal) {
                // Only update if significantly different (avoid feedback loops)
                const dx = Math.abs(this._internalTranslate.x - newVal.x);
                const dy = Math.abs(this._internalTranslate.y - newVal.y);
                if (dx > 1 || dy > 1) {
                    this._internalTranslate.x = newVal.x;
                    this._internalTranslate.y = newVal.y;
                    this._applyTransformToDOM();
                }
                // Update minimap
                if (this.minimapVisible) {
                    this.debouncedMinimapUpdate();
                }
            },
            deep: true,
        },
        scale: {
            handler(newVal) {
                // Only update if significantly different
                if (Math.abs(this._internalScale - newVal) > 0.01) {
                    this._internalScale = newVal;
                    this._applyTransformToDOM();
                }
                if (this.minimapVisible) {
                    this.debouncedMinimapUpdate();
                }
            },
        },
        // Keep only essential watchers - removed all debug-only watchers
        navigation: {
            handler() {
                // Navigation changes might need blocks recalculation
            },
            deep: false,
        },
    },
};
</script>

<style lang="scss" scoped>
.diagram-viewer {
    //height: calc(100vh);
    height: calc(100vh - 55px);
    background-image: linear-gradient(rgba(214, 214, 219, 0.2) 1px, transparent 1px),
        linear-gradient(to right, rgba(214, 214, 219, 0.4) 1px, rgba(139, 140, 140, 0.1) 1px) !important;
    background-size: 20px 20px;
    cursor: grab;
    
    &.is-grabbing {
        cursor: grabbing;
    }

    .wrapper {
        height: 100%;
        width: 100%;
        // GPU acceleration for smooth transforms
        will-change: transform;
        transform-origin: 0 0;
    }

    // Automation Mini-map unique styles (performance optimized)
    .automation-minimap-container {
        position: absolute;
        bottom: 70px;
        left: 15px;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
        border: 1px solid rgba(226, 232, 240, 0.8);
        border-radius: 16px;
        box-shadow:
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04),
            0 0 0 1px rgba(255, 255, 255, 0.05);
        z-index: 1000;
        backdrop-filter: blur(20px) saturate(180%);
        transition: transform 0.2s ease, opacity 0.2s ease;
        overflow: hidden;
        will-change: transform, opacity;
        transform: translateZ(0); // Hardware acceleration
        contain: layout style paint;

        &:hover {
            transform: translateY(-2px);
            box-shadow:
                0 25px 50px -12px rgba(0, 0, 0, 0.15),
                0 20px 20px -5px rgba(0, 0, 0, 0.06),
                0 0 0 1px rgba(255, 255, 255, 0.1);
        }

        .automation-minimap-content {
            padding: 8px;
            cursor: crosshair;
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            border-radius: 16px;
            will-change: transform;
            contain: layout style paint;
            transform: translateZ(0);

            svg {
                display: block;
                background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
                border: 2px solid rgba(148, 163, 184, 0.2);
                border-radius: 12px;
                box-shadow:
                    inset 0 2px 4px rgba(0, 0, 0, 0.06),
                    0 1px 3px rgba(0, 0, 0, 0.1);
                transition: all 0.2s ease;

                &:hover {
                    border-color: rgba(99, 102, 241, 0.3);
                    box-shadow:
                        inset 0 2px 4px rgba(0, 0, 0, 0.06),
                        0 4px 6px rgba(0, 0, 0, 0.1),
                        0 0 0 3px rgba(99, 102, 241, 0.1);
                }
            }

            .automation-minimap-block {
                stroke: rgba(255, 255, 255, 0.6);
                stroke-width: 0.3;
                opacity: 0.8;
                transition: none; // Transition'ları kaldır
                will-change: auto; // will-change'i kaldır

                &:hover {
                    opacity: 0.9; // Minimal hover efekti
                }

                &.automation-selected {
                    opacity: 1;
                    stroke: #3b82f6;
                    stroke-width: 1;
                    // Animasyonları kaldır
                }
            }

            .automation-minimap-viewport {
                pointer-events: none;
                opacity: 0.6;
                stroke: #3b82f6;
                stroke-width: 1;
                stroke-dasharray: 2,1;
                fill: rgba(59, 130, 246, 0.05);
                // Animasyonları kaldır
            }
        }
    }

    // Animasyonları kaldırdık - performans için
}
</style>
